import { AnimatePresence, useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useIntl } from 'react-intl';

import { projectThinkingMessages } from './projectThinking.messages';

/**
 * Initial thinking indicator for Projects surfaces.
 *
 * A single status line that crossfades through warm-up phrases while a
 * continuous text shimmer (`ptb-text-shimmer`, defined in globals.css) sweeps
 * across it. Each phrase flips up: the outgoing phrase rises and fades out the
 * top while the next rises in from below.
 *
 * Shown the instant a fresh prompt lands, before the agent has any context to
 * work with — i.e. the `ProjectChat` thinking block and the cold-start state of
 * `ComputerUseTaskBlock` (before the first real planner/executor step) — and as
 * a live `WorkingBlock`'s line while it has nothing to name.
 */

// Masks the text and sweeps the mask across it. Pairs with the
// `ptb-text-shimmer` keyframe in src/styles/globals.css.
const SHIMMER_MASK =
  'linear-gradient(90deg, #000 0%, #000 35%, rgba(0,0,0,0.35) 50%, #000 65%, #000 100%)';

const SHIMMER_STYLE: CSSProperties = {
  maskImage: SHIMMER_MASK,
  maskSize: '200% 100%',
  WebkitMaskImage: SHIMMER_MASK,
  WebkitMaskSize: '200% 100%',
  animation: 'ptb-text-shimmer 2s ease-in-out infinite',
};

export const THINKING_MESSAGE_DESCRIPTORS = [
  projectThinkingMessages.scrubbingIn,
  projectThinkingMessages.preppingForRounds,
  projectThinkingMessages.reviewingTheAsk,
  projectThinkingMessages.workingItUp,
] as const;

export const PHRASE_DURATION_MS = 4000;

// Matches design-system `p2` (14px / text-sm), the same size as Work chat
// and overlay assistant body copy. Shared on purpose across every consumer.
const PHRASE_TEXT_CLASS =
  'block whitespace-nowrap text-sm font-normal leading-normal tracking-normal text-text-secondary';

interface ProjectThinkingIndicatorProps {
  /**
   * Optional contextual phrase pool delivered over agent-events SSE.
   *
   * We apply pool changes at the next natural flip boundary (latest-wins) to avoid
   * interrupting the phrase currently on screen.
   */
  messages?: readonly string[];
}

interface PhrasePool {
  id: string;
  phrases: readonly string[];
}

export const ProjectThinkingIndicator = ({
  messages,
}: ProjectThinkingIndicatorProps = {}): JSX.Element => {
  const intl = useIntl();
  const reduce = useReducedMotion();

  const genericPhrases = useMemo(
    () =>
      THINKING_MESSAGE_DESCRIPTORS.map((descriptor) => intl.formatMessage(descriptor)),
    [intl]
  );
  const incomingPhrases = useMemo(
    () => (messages && messages.length > 0 ? messages : genericPhrases),
    [messages, genericPhrases]
  );
  const incomingPoolId = useMemo(
    () => (messages && messages.length > 0 ? messages.join('\0') : 'generic'),
    [messages]
  );

  const [activePhrases, setActivePhrases] = useState(incomingPhrases);
  const [activePoolId, setActivePoolId] = useState(incomingPoolId);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const activePhrasesRef = useRef(activePhrases);
  const pendingPoolRef = useRef<PhrasePool | null>(null);

  useEffect(() => {
    activePhrasesRef.current = activePhrases;
  }, [activePhrases]);

  useEffect(() => {
    if (incomingPoolId === activePoolId) return;
    pendingPoolRef.current = { id: incomingPoolId, phrases: incomingPhrases };
  }, [incomingPoolId, incomingPhrases, activePoolId]);

  useEffect(() => {
    const id = setInterval(() => {
      const pendingPool = pendingPoolRef.current;
      if (pendingPool) {
        pendingPoolRef.current = null;
        setActivePhrases(pendingPool.phrases);
        setActivePoolId(pendingPool.id);
        setPhraseIndex(0);
        return;
      }

      setPhraseIndex((curr) => {
        const phraseCount = activePhrasesRef.current.length;
        return phraseCount > 1 ? (curr + 1) % phraseCount : 0;
      });
    }, PHRASE_DURATION_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (phraseIndex < activePhrases.length) return;
    setPhraseIndex(0);
  }, [phraseIndex, activePhrases.length]);

  const phraseText = activePhrases[phraseIndex] ?? activePhrases[0] ?? '';

  return (
    <div className="relative flex w-full items-center gap-1 overflow-hidden">
      {/* `initial={false}`: the first phrase is the send's acknowledgement, so it
          paints at full opacity in the commit that mounts it. Only later flips
          animate. */}
      <AnimatePresence initial={false} mode="popLayout">
        <m.span
          key={`${activePoolId}:${phraseIndex}`}
          initial={reduce ? false : { y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={reduce ? { opacity: 0 } : { y: '-100%', opacity: 0 }}
          transition={{ duration: 0.28, ease: [0.45, 0, 0.55, 1] }}
          style={reduce ? undefined : SHIMMER_STYLE}
          className={PHRASE_TEXT_CLASS}
        >
          {phraseText}
        </m.span>
      </AnimatePresence>
    </div>
  );
};
